import { createColumnHelper } from '@tanstack/react-table'
import { getCommonContactCompanyColumns } from 'components/DataExpansionComponents/Common/CommonTable/CommonColumns'
import { TruncatedExternalLinkCell } from 'components/Tables/CommonTableCells/TruncatedExternalLinkCell'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { K12ChainProxy } from 'models/k12'
import { useMemo } from 'react'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import {
  integerFormatterOrFallback,
  percentFormatterOrFallback,
} from 'utils/formatting'
import { DataTableColumnSort } from '../../../DataTable/DataTableSorting/DataTableSortingStore'
import { TruncatedLinkCell } from 'components/Tables/CommonTableCells/TruncatedLinkCell'
import { PlaceUrlIcon } from 'components/MenuIcon/PlaceUrlIcon'
import { K12_COLUMNS } from '../K12ColumnsInfo'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { PhoneCell } from 'components/Tables/CommonTableCells/PhoneCell'

export const useK12TableColumns = ({
  sorting,
  inCampaigns,
}: {
  sorting?: DataTableColumnSort[]
  inCampaigns?: boolean
}) => {
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const columnHelper = createColumnHelper<K12ChainProxy>()
  const featureFlag = useFeatureFlagService()
  const k12ColumnHelper = createTableColumnHelper({
    columns: K12_COLUMNS,
    tooltips,
  })

  const columns = useMemo(() => {
    let cols = [
      columnHelper.accessor('k12district.lea_name', {
        ...k12ColumnHelper.retrieveColumn('lea_name', {
          meta: {
            isEditable: false,
            isFirstColumn: true,
          },
        }),
        header: () => null,
        size: 380,
        cell: (info) => {
          const name = info.row.original?.k12district
            ? info.getValue()
            : info.row.original.name
          if (info.row.original?.k12district) {
            return (
              <TruncatedLinkCell
                text={name || ''}
                to={`/k12/${preferences?.company_type_prefix_map[ContactCompanyType.K12District]}${info.row.original?.k12district?.id}`}
              />
            )
          } else {
            return (
              <TruncatedLinkCell
                text={info.row.original.name || ''}
                to={`/company/${preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]}${info.row.original?.id}`}
              />
            )
          }
        },
      }),
      columnHelper.accessor('k12district.address', {
        ...k12ColumnHelper.retrieveColumn('address'),
        size: 200,
      }),
      columnHelper.accessor('k12district.zipcode', {
        ...k12ColumnHelper.retrieveColumn('zipcode'),
        size: 100,
      }),
      columnHelper.accessor('k12district.lcity', {
        ...k12ColumnHelper.retrieveColumn('lcity'),
        size: 150,
      }),
      columnHelper.accessor('k12district.lstate', {
        ...k12ColumnHelper.retrieveColumn('lstate'),
        size: 100,
      }),
      columnHelper.accessor('k12district.county', {
        ...k12ColumnHelper.retrieveColumn('county'),
        size: 200,
      }),
      columnHelper.accessor('k12district.locale', {
        ...k12ColumnHelper.retrieveColumn('locale'),
        size: 200,
      }),
      columnHelper.accessor('k12district.operational_schools', {
        ...k12ColumnHelper.retrieveColumn('operational_schools'),
        size: 150,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.gslo', {
        ...k12ColumnHelper.retrieveColumn('gslo'),
        size: 100,
      }),
      columnHelper.accessor('k12district.gshi', {
        ...k12ColumnHelper.retrieveColumn('gshi'),
        size: 100,
      }),
      columnHelper.accessor('k12district.student_count', {
        ...k12ColumnHelper.retrieveColumn('student_count'),
        size: 150,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.teachers_count', {
        ...k12ColumnHelper.retrieveColumn('teachers_count'),
        size: 150,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.phone', {
        ...k12ColumnHelper.retrieveColumn('phone'),
        size: 150,
        cell: (info) => {
          const phone = info.getValue()
          return <PhoneCell phoneNumber={phone} />
        },
      }),
      ...getCommonContactCompanyColumns(
        columnHelper,
        sorting,
        preferences,
        inCampaigns
      ),
      columnHelper.accessor('k12district.revenue', {
        ...k12ColumnHelper.retrieveColumn('revenue'),
        size: 200,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.reduced_lunch', {
        ...k12ColumnHelper.retrieveColumn('reduced_lunch'),
        size: 250,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.free_lunch', {
        ...k12ColumnHelper.retrieveColumn('free_lunch'),
        size: 250,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.estimated_meals_per_day', {
        ...k12ColumnHelper.retrieveColumn('estimated_meals_per_day'),
        size: 200,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.food_service_expenditures', {
        ...k12ColumnHelper.retrieveColumn('food_service_expenditures'),
        size: 200,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.total_supplemented_lunch', {
        ...k12ColumnHelper.retrieveColumn('total_supplemented_lunch'),
        size: 250,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.total_supplemented_lunch_perc', {
        ...k12ColumnHelper.retrieveColumn('total_supplemented_lunch_perc'),
        size: 250,
        cell: (info) => {
          return percentFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.domain', {
        ...k12ColumnHelper.retrieveColumn('domain'),
        size: 200,
        cell: (info) => {
          const domain = info.getValue()
          if (!domain) {
            return '-'
          }
          return (
            <TruncatedExternalLinkCell text={domain} href={'//' + domain} />
          )
        },
      }),
      columnHelper.accessor('k12district.google_place_url', {
        ...k12ColumnHelper.retrieveColumn('google_place_url'),
        size: 80,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '-'
          return (
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              aria-label="Open place URL"
              className="inline-flex items-center text-neutral-700 hover:text-neutral-900"
            >
              <PlaceUrlIcon size={20} />
            </a>
          )
        },
      }),
      columnHelper.accessor('k12district.uncategorized_lunch', {
        ...k12ColumnHelper.retrieveColumn('uncategorized_lunch'),
        size: 250,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.free_lunch_perc', {
        ...k12ColumnHelper.retrieveColumn('free_lunch_perc'),
        size: 250,
        cell: (info) => {
          return percentFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.reduced_lunch_perc', {
        ...k12ColumnHelper.retrieveColumn('reduced_lunch_perc'),
        size: 250,
        cell: (info) => {
          return percentFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.uncategorized_lunch_perc', {
        ...k12ColumnHelper.retrieveColumn('uncategorized_lunch_perc'),
        size: 250,
        cell: (info) => {
          return percentFormatterOrFallback(info.getValue()) || '-'
        },
      }),
    ]

    if (!featureFlag.enableCampaigns) {
      cols = cols.filter((col) => !col.id?.includes('campaigns'))
    }
    return cols
  }, [
    columnHelper,
    sorting,
    preferences,
    inCampaigns,
    featureFlag,
    k12ColumnHelper,
  ])

  return columns
}
